import { cn } from "@nt/ui/cn";
import { buttonVariants } from "@nt/ui/recipes";
import { EmptyState } from "@nt/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStack,
  TableStackField,
  TableStackFields,
  TableStackRow,
  TableStackTitle,
} from "@nt/ui/table";
import { Link2, MoreHorizontal } from "lucide-react";
import { useEffect, useId, useRef, type Ref, type ReactNode } from "react";

import { CopyField } from "@/components/copy-field";
import { Section } from "@/components/section";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@nt/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@nt/ui/tooltip";
import { messageOf } from "@/lib/api";
import { shortDate } from "@/lib/format";
import type { LinkRow } from "@/lib/types";

const READ_ONLY_REASON = "Your account is read-only";

/** "Create link": disabled with a tooltip (and a screen-reader reason) for a read-only partner. */
function CreateLinkButton({
  readOnly,
  onClick,
}: {
  readOnly: boolean;
  onClick: () => void;
}) {
  if (!readOnly) {
    return (
      <button type="button" onClick={onClick} className={buttonVariants()}>
        Create link
      </button>
    );
  }
  // A disabled button gets no pointer or focus events, so the span carries the tooltip.
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span tabIndex={0} className="inline-flex rounded-full" />}
      >
        <button type="button" disabled className={buttonVariants()}>
          Create link
          <span className="sr-only">. {READ_ONLY_REASON}.</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>{READ_ONLY_REASON}</TooltipContent>
    </Tooltip>
  );
}

/** The inline "are you sure" before a link is archived. */
export function ArchiveConfirm({
  link,
  pending,
  error,
  onConfirm,
  onCancel,
}: {
  link: LinkRow;
  pending: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => confirmRef.current?.focus(), []);
  return (
    <div
      role="alertdialog"
      aria-labelledby={titleId}
      className="mx-cell-x mb-4 grid gap-3 rounded-control bg-muted p-4"
    >
      <div className="grid gap-1">
        <p id={titleId} className="text-sm font-semibold text-foreground">
          Archive {link.name}?
        </p>
        <p className="text-sm text-muted-foreground">
          It leaves this list and you cannot restore it. People who already have
          the link still reach the page and still count for you.
        </p>
      </div>
      {error ? (
        <p role="alert" className="text-sm font-medium text-danger">
          {messageOf(error)}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          ref={confirmRef}
          type="button"
          disabled={pending}
          onClick={onConfirm}
          className={buttonVariants({ variant: "danger-soft", size: "sm" })}
        >
          {pending ? "Archiving…" : "Archive link"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
        >
          Keep it
        </button>
      </div>
    </div>
  );
}

function RowMenu({
  link,
  readOnly,
  onCopy,
  onArchive,
}: {
  link: LinkRow;
  readOnly: boolean;
  onCopy: (link: LinkRow) => void;
  onArchive: (link: LinkRow) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Actions for ${link.name}`}
        className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
      >
        <MoreHorizontal aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-auto min-w-40">
        <DropdownMenuItem onClick={() => onCopy(link)}>
          Copy link
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          disabled={readOnly}
          onClick={() => onArchive(link)}
        >
          Archive
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface CustomLinksProps {
  links: LinkRow[];
  readOnly: boolean;
  onCreate: () => void;
  onCopy: (link: LinkRow) => void;
  onArchive: (link: LinkRow) => void;
  /** The copy button of each row, so a new link's button can take focus. */
  copyRef: (id: string) => Ref<HTMLButtonElement>;
  /** A status line ("Link created") above the list. */
  notice?: { tone: "success" | "danger"; text: string } | null;
  /** The archive confirmation, when one is open. */
  confirm?: ReactNode;
}

function Name({ link }: { link: LinkRow }) {
  return (
    <div className="grid min-w-0 gap-0.5">
      <span className="font-medium text-foreground">{link.name}</span>
      {link.note ? (
        <span className="text-compact text-muted-foreground">{link.note}</span>
      ) : null}
    </div>
  );
}

function LinksTable({
  links,
  readOnly,
  onCopy,
  onArchive,
  copyRef,
}: CustomLinksProps) {
  return (
    <div className="hidden lg:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Link</TableHead>
            <TableHead>Destination</TableHead>
            <TableHead className="text-right">Clicks</TableHead>
            <TableHead className="text-right">Sign-ups</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {links.map((link) => (
            <TableRow key={link.id}>
              <TableCell className="max-w-60 min-w-40 normal-nums">
                <Name link={link} />
              </TableCell>
              <TableCell className="w-64 min-w-48">
                <CopyField
                  compact
                  hideLabel
                  label={`Link for ${link.name}`}
                  value={link.url}
                  copyRef={copyRef(link.id)}
                />
              </TableCell>
              <TableCell className="font-mono text-compact">
                {link.destination}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {link.clicks30d}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {link.signups30d}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {shortDate(link.createdAt)}
              </TableCell>
              <TableCell className="w-10 text-right">
                <RowMenu
                  link={link}
                  readOnly={readOnly}
                  onCopy={onCopy}
                  onArchive={onArchive}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LinksStack({
  links,
  readOnly,
  onCopy,
  onArchive,
  copyRef,
}: CustomLinksProps) {
  return (
    <TableStack className="lg:hidden">
      {links.map((link) => (
        <TableStackRow key={link.id}>
          <TableStackTitle>
            <Name link={link} />
            <RowMenu
              link={link}
              readOnly={readOnly}
              onCopy={onCopy}
              onArchive={onArchive}
            />
          </TableStackTitle>
          <CopyField
            compact
            hideLabel
            label={`Link for ${link.name}`}
            value={link.url}
            copyRef={copyRef(link.id)}
          />
          <TableStackFields>
            <TableStackField label="Destination">
              <span className="font-mono">{link.destination}</span>
            </TableStackField>
            <TableStackField label="Clicks (30 days)">
              {link.clicks30d}
            </TableStackField>
            <TableStackField label="Sign-ups (30 days)">
              {link.signups30d}
            </TableStackField>
            <TableStackField label="Created">
              {shortDate(link.createdAt)}
            </TableStackField>
          </TableStackFields>
        </TableStackRow>
      ))}
    </TableStack>
  );
}

/** "Custom links": the create button, a status line, then the table (a stack on a phone) or the empty state. */
export function CustomLinksSection(props: CustomLinksProps) {
  const { links, readOnly, onCreate, notice, confirm } = props;
  return (
    <Section
      id="custom-links"
      title="Custom links"
      description="A link per place you share, each to the page you choose. Clicks and sign-ups are for the last 30 days."
      actions={
        links.length > 0 ? (
          <CreateLinkButton readOnly={readOnly} onClick={onCreate} />
        ) : null
      }
      contentClassName="px-0"
    >
      {notice ? (
        <p
          role="status"
          className={cn(
            "mx-cell-x mb-4 rounded-control px-3 py-2 text-sm font-medium",
            notice.tone === "success"
              ? "bg-success-tint text-success"
              : "bg-danger-tint text-danger",
          )}
        >
          {notice.text}
        </p>
      ) : null}
      {confirm}
      {links.length === 0 ? (
        <EmptyState
          icon={<Link2 />}
          title="No custom links yet"
          description="Create one for each video, post or newsletter to see which brings sign-ups. Your main link keeps working either way."
          action={<CreateLinkButton readOnly={readOnly} onClick={onCreate} />}
        />
      ) : (
        <>
          <LinksTable {...props} />
          <LinksStack {...props} />
        </>
      )}
    </Section>
  );
}
